import React, { useEffect, useState, createContext, useContext } from 'react';
import { login as apiLogin, listResortAdminUsers, createResortAdminUser, updateResortAdminUser, deleteResortAdminUser, changeOwnPassword, whoami } from '../services/resortAdminApi';

// Define the shape of our user object
interface User {
  id: string;
  email: string;
  name: string;
  role: string; // 'Resort Super Admin' | 'Supervisor' | 'Editor' | 'Partner Admin' | 'Partner Courier' | 'Support'
  scope_type?: 'restaurant' | 'grocery' | 'all';
  restaurant?: string;
  grocery?: string;
}

// Define the shape of our auth context
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isReadOnly: boolean;
  users: User[];
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  addUser: (email: string, password: string, role: 'Resort Super Admin' | 'Editor' | 'Partner Admin' | 'Partner Courier' | 'Support', scope_type: 'restaurant' | 'grocery', restaurant?: string, grocery?: string) => Promise<boolean>;
  deleteUser: (id: string) => Promise<boolean>;
  updateUserRole: (id: string, role: 'Resort Super Admin' | 'Editor' | 'Partner Admin' | 'Partner Courier' | 'Support') => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isReadOnly: false,
  users: [],
  login: async () => false,
  logout: () => {
    // the provider supplies the real implementation; this only fills the context type
  },
  addUser: async () => false,
  deleteUser: async () => false,
  updateUserRole: async () => false,
  changePassword: async () => false
});

export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  // Initialize authentication state by checking localStorage immediately
  const [user, setUser] = useState<User | null>(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('auth_token');
      if (storedUser && storedToken) {
        const parsedUser = JSON.parse(storedUser);
        console.log('Found stored user:', parsedUser); // Debug log
        return parsedUser;
      }
    } catch (error) {
      console.error('Error retrieving stored user:', error);
      // Clear potentially corrupted data
      localStorage.removeItem('user');
      localStorage.removeItem('auth_token');
    }
    return null;
  });
  
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('auth_token');
      return !!(storedUser && storedToken);
    } catch (error) {
      return false;
    }
  });
  
  const [users, setUsers] = useState<User[]>([]);

  // Load users from API
  useEffect(() => {
    if (isAuthenticated && (user?.role === 'Resort Super Admin' || user?.role === 'Supervisor')) {
      loadUsers();
    }
  }, [isAuthenticated, user]);

  // Listen for auth-expired events from API interceptor
  useEffect(() => {
    const handleAuthExpired = () => {
      console.log('Authentication expired, logging out...');
      logout();
    };

    window.addEventListener('auth-expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, []);

  // Reconcile the cached user's role/scope with the server on load. The
  // login-time role lives in localStorage and goes stale if an admin changes
  // this user's role; without re-syncing, the UI keeps gating on the old role
  // (e.g. showing User Management to a user the backend has downgraded, which
  // then returns nothing -> blank/empty screen). whoami reflects the effective
  // (token) role, so the UI gating stays consistent with what the backend
  // actually authorizes.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await whoami();
        if (!cancelled && me && me.role) {
          setUser(prev => {
            if (prev && prev.role === me.role && prev.scope_type === me.scope_type) {
              return prev;
            }
            const next: User = {
              id: me.email,
              email: me.email,
              name: me.email,
              role: me.role,
              scope_type: me.scope_type,
              restaurant: prev?.restaurant,
              grocery: prev?.grocery,
            };
            try { localStorage.setItem('user', JSON.stringify(next)); } catch { /* localStorage unavailable; non-fatal */ }
            return next;
          });
        }
      } catch (error) {
        // A failed whoami (e.g. expired/invalid token) is handled by the api
        // interceptor's auth-expired flow; nothing to do here.
        console.error('Failed to sync user from server:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Pick up role changes applied by a silent token refresh: the api layer
  // updates the cached user (localStorage) and emits this event.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && detail.role) setUser(detail);
    };
    window.addEventListener('auth-user-updated', handler);
    return () => window.removeEventListener('auth-user-updated', handler);
  }, []);

  const loadUsers = async () => {
    try {
      const result = await listResortAdminUsers();
      if (result && result.data) {
        const apiUsers = result.data.map((u: any) => ({
          id: u.name,
          email: u.email,
          name: u.email,
          role: u.role as 'Resort Super Admin' | 'Editor' | 'Partner Admin' | 'Partner Courier' | 'Support',
          scope_type: u.scope_type,
          restaurant: u.restaurant,
          grocery: u.grocery,
        }));
        setUsers(apiUsers);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Login function
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      console.log('Login attempt:', email); // Debug log
      const result = await apiLogin(email, password);

      if (result && result.jwt) {
        // Block courier-only roles from accessing the admin panel
        const courierOnlyRoles = ['Restaurant Courier', 'Grocery Courier', 'Partner Courier'];
        if (courierOnlyRoles.includes(result.role)) {
          throw new Error('Courier accounts cannot access the admin panel. Please use the Courier App.');
        }

        const userData = {
          id: email,
          email: email,
          name: email,
          role: result.role as 'Resort Super Admin' | 'Editor' | 'Partner Admin' | 'Partner Courier' | 'Support',
          scope_type: result.scope_type,
          restaurant: result.restaurant,
          grocery: result.grocery,
        };

        setUser(userData);
        setIsAuthenticated(true);

        // Save to localStorage
        try {
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('auth_token', result.jwt);
          localStorage.setItem('auth_token_expires_at', new Date(Date.now() + result.expires_in * 1000).toISOString());

          // Save refresh token if provided
          if (result.refresh_token) {
            localStorage.setItem('refresh_token', result.refresh_token);
            localStorage.setItem('refresh_token_expires_at', new Date(Date.now() + result.refresh_token_expires_in * 1000).toISOString());
          }
        } catch (error) {
          console.error('Error saving user to localStorage:', error);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      // Re-throw role validation errors so Login page can display them
      if (error instanceof Error && error.message.includes('cannot access')) {
        throw error;
      }
      return false;
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_token_expires_at');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('refresh_token_expires_at');
    try {
      sessionStorage.removeItem('auth_expired');
      sessionStorage.removeItem('auth_expired_path');
    } catch {
      // storage is unavailable in some privacy modes, and this path must not fail because of it
    }
  };

  // Add user function
  const addUser = async (email: string, password: string, role: string, scope_type: 'restaurant' | 'grocery', restaurant?: string, grocery?: string): Promise<boolean> => {
    try {
      const result = await createResortAdminUser({
        email,
        password,
        status: 'Active',
        role,
        scope_type,
        restaurant,
        grocery,
      });
      
      if (result && result.name) {
        // Reload users to get updated list
        await loadUsers();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Add user error:', error);
      return false;
    }
  };

  // Delete user function
  const deleteUser = async (id: string): Promise<boolean> => {
    try {
      // Prevent deleting yourself
      if (user?.id === id) {
        return false;
      }
      
      const result = await deleteResortAdminUser(id);
      if (result) {
        // Reload users to get updated list
        await loadUsers();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Delete user error:', error);
      return false;
    }
  };

  // Update user role function
  const updateUserRole = async (id: string, role: 'Resort Super Admin' | 'Editor' | 'Partner Admin' | 'Partner Courier' | 'Support'): Promise<boolean> => {
    try {
      const result = await updateResortAdminUser({
        name: id,
        role: role
      });
      
      if (result) {
        // Reload users to get updated list
        await loadUsers();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Update user role error:', error);
      return false;
    }
  };

  // Change password function
  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      if (!user) return false;

      const result = await changeOwnPassword(currentPassword, newPassword);

      return result ? true : false;
    } catch (error) {
      console.error('Change password error:', error);
      return false;
    }
  };

  const isReadOnly = user?.role === 'Supervisor';

  return <AuthContext.Provider value={{
    user,
    isAuthenticated,
    isReadOnly: !!isReadOnly,
    users,
    login,
    logout,
    addUser,
    deleteUser,
    updateUserRole,
    changePassword
  }}>
      {children}
    </AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);
